// js/accounts/auth.js
// Flacdium account manager. Replaces the old cloud (PocketBase/Appwrite/OAuth) auth with
// Flacdium's shared accounts: login via /api/auth/login using a bearer token. The token
// is what authorizes streaming/downloads (see flacdium-api.js mapTrack / player.js).
// Self-signup is intentionally disabled here — accounts are provisioned by a Flacdium
// admin; the web player only offers login.
import { flacdiumLogin, flacdiumMe, flacdiumLogout } from '../flacdium-api.js';

function toUser(me) {
    if (!me || !me.username) return null;
    return { id: me.username, name: me.username, username: me.username, email: me.username, isAdmin: !!me.is_admin };
}

export class AuthManager {
    constructor() {
        this.user = null;
        this.authListeners = [];
        this.init().catch(console.error);
    }

    async init() {
        // No token → no network call (fast boot). With a token, validate it once.
        try {
            this.user = toUser(await flacdiumMe());
        } catch {
            this.user = null;
        }
        this.updateUI(this.user);
        this.notify();
    }

    notify() {
        this.authListeners.forEach((listener) => listener(this.user));
    }

    onAuthStateChanged(callback) {
        this.authListeners.push(callback);
        if (this.user !== null) callback(this.user);
    }

    // Primary Flacdium auth. `signInWithEmail` is kept as an alias so the existing email
    // auth UI keeps working — the "email" field is the Flacdium username.
    async login(username, password) {
        const { username: name } = await flacdiumLogin(username, password);
        this.user = toUser({ username: name });
        this.updateUI(this.user);
        this.notify();
        return this.user;
    }

    signInWithEmail(username, password) {
        return this.login(username, password);
    }

    async signOut() {
        flacdiumLogout();
        this.user = null;
        this.updateUI(null);
        this.notify();
        // Reload so already-rendered tracks drop their (now invalid) stream token and the
        // auth gate comes back up.
        window.location.reload();
    }

    // Bind the full-screen login gate (the auth wall). Login only — self-signup is
    // disabled; accounts are provisioned by a Flacdium admin. Called once at boot.
    setupGate() {
        const gate = document.getElementById('flacdium-auth-gate');
        const loginForm = document.getElementById('flacdium-login-form');
        if (!gate || !loginForm || gate.dataset.bound) return;
        gate.dataset.bound = '1';

        const runForm = async (form, errEl, fn) => {
            if (errEl) errEl.textContent = '';
            const buttons = form.querySelectorAll('button');
            buttons.forEach((b) => (b.disabled = true));
            try {
                await fn();
                // Fresh boot with the new token: gate hidden, library loads authorized.
                window.location.reload();
            } catch (e) {
                if (errEl) errEl.textContent = e?.message || 'Đăng nhập thất bại.';
                buttons.forEach((b) => (b.disabled = false));
            }
        };

        const loginErrEl = document.getElementById('gate-error');
        loginForm.addEventListener('submit', (e) => {
            e.preventDefault();
            const u = (document.getElementById('gate-username')?.value || '').trim();
            const p = document.getElementById('gate-password')?.value || '';
            if (!u || !p) {
                if (loginErrEl) loginErrEl.textContent = 'Nhập tên đăng nhập và mật khẩu.';
                return;
            }
            void runForm(loginForm, loginErrEl, () => this.login(u, p));
        });
    }

    updateUI(user) {
        // Auth wall: show the gate whenever there is no signed-in user.
        const gate = document.getElementById('flacdium-auth-gate');
        if (gate) gate.classList.toggle('hidden', !!user);

        const statusText = document.getElementById('auth-status');
        if (statusText) {
            statusText.textContent = user ? `Signed in as ${user.username}` : 'Not signed in.';
        }
        const connectBtn = document.getElementById('auth-connect-btn');
        if (connectBtn) {
            connectBtn.textContent = 'Sign Out';
            connectBtn.classList.add('danger');
            connectBtn.style.display = user ? '' : 'none';
            connectBtn.onclick = () => this.signOut();
        }
    }
}

export const authManager = new AuthManager();
